import { useState, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  Trash2,
  Play,
  ChevronRight,
  AlertCircle,
  FileUp,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { getCategories, getWords, deleteCategory } from "@/hooks/useFirestore";
import { ExportDialog } from "@/components/ExportDialog";
import { useAuth } from "@/contexts/AuthContext";
import { Word } from "@/types";
import { calculateStrength, cn } from "@/lib/utils";

export function CategoryDetailPage() {
  const { user } = useAuth();
  const { categoryId } = useParams<{ categoryId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showExportDialog, setShowExportDialog] = useState(false);

  const { data: categories, isLoading: categoriesLoading } = useQuery({
    queryKey: ["categories", user?.uid],
    queryFn: () => getCategories(user!.uid),
    enabled: !!user,
  });

  const category = categories?.find((c) => c.id === categoryId);

  const { data: allWords, isLoading: wordsLoading } = useQuery({
    queryKey: ["words", user?.uid],
    queryFn: () => getWords(user!.uid),
    enabled: !!user,
  });

  // Filter words by category
  const words = useMemo(() => {
    if (!allWords || !category) return [];
    return allWords.filter((w) => w.categories.includes(category.name));
  }, [allWords, category]);

  // Calculate stats
  const stats = useMemo(() => {
    if (!words) return { wordCount: 0, reviewedCount: 0, successRate: 0 };

    let reviewedCount = 0;
    let totalKnown = 0;
    let totalUnknown = 0;

    words.forEach((word) => {
      const reviewed = word.reviewStats.knownCount + word.reviewStats.unknownCount;
      if (reviewed > 0) {
        reviewedCount++;
        totalKnown += word.reviewStats.knownCount;
        totalUnknown += word.reviewStats.unknownCount;
      }
    });

    const totalReviews = totalKnown + totalUnknown;
    const successRate = totalReviews > 0 ? Math.round((totalKnown / totalReviews) * 100) : 0;

    return {
      wordCount: words.length,
      reviewedCount,
      successRate,
    };
  }, [words]);

  const deleteMutation = useMutation({
    mutationFn: () => deleteCategory(user!.uid, categoryId!, category!.name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["categories"] });
      queryClient.invalidateQueries({ queryKey: ["words"] });
      navigate("/categories");
    },
  });

  if (categoriesLoading || wordsLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!category) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
        <p className="text-muted-foreground">Category not found</p>
        <Button variant="ghost" onClick={() => navigate("/categories")} className="mt-4">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Categories
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={() => navigate("/categories")}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            onClick={() => setShowExportDialog(true)}
            disabled={words.length === 0}
          >
            <FileUp className="mr-2 h-4 w-4" />
            Export
          </Button>
          <Button
            variant="ghost"
            className="text-destructive hover:text-destructive"
            onClick={() => setShowDeleteDialog(true)}
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Delete
          </Button>
        </div>
      </div>

      {/* Category Header */}
      <Card>
        <CardContent className="pt-6">
          <div className="text-center space-y-2">
            <h1 className="text-2xl font-semibold">{category.name}</h1>
            <p className="text-muted-foreground">{stats.wordCount} words</p>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-4 text-center">
            <div className="text-3xl font-bold">{stats.wordCount}</div>
            <div className="text-sm text-muted-foreground">Words</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 text-center">
            <div className="text-3xl font-bold">{stats.reviewedCount}</div>
            <div className="text-sm text-muted-foreground">Reviewed</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 text-center">
            <div className="text-3xl font-bold">{stats.successRate}%</div>
            <div className="text-sm text-muted-foreground">Success</div>
          </CardContent>
        </Card>
      </div>

      {/* Review Button */}
      <Button
        className="w-full"
        size="lg"
        onClick={() => navigate(`/practice?category=${encodeURIComponent(category.name)}`)}
        disabled={stats.wordCount === 0}
      >
        <Play className="mr-2 h-5 w-5" />
        Review This Category
      </Button>

      {/* All Words */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">All Words</CardTitle>
        </CardHeader>
        <CardContent>
          {words.length === 0 ? (
            <p className="text-center text-muted-foreground py-4">
              No words in this category
            </p>
          ) : (
            <div className="space-y-2">
              {words.map((word) => (
                <WordRow key={word.id} word={word} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Export Dialog */}
      <ExportDialog
        open={showExportDialog}
        onOpenChange={setShowExportDialog}
        words={words}
        name={category.name}
      />

      {/* Delete Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Category</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{category.name}"?
              This will remove the category from all words but won't delete the words themselves.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteMutation.mutate()}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function WordRow({ word }: { word: Word }) {
  const strength = calculateStrength(
    word.reviewStats.knownCount,
    word.reviewStats.unknownCount
  );

  return (
    <Link to={`/words/${word.id}`}>
      <div className="flex items-center justify-between p-2 rounded-md hover:bg-muted/50 cursor-pointer">
        <div className="flex items-center gap-3">
          <div
            className={cn(
              "w-2 h-2 rounded-full",
              strength === "weak" && "bg-red-500",
              strength === "learning" && "bg-amber-500",
              strength === "strong" && "bg-green-500"
            )}
          />
          <span className="font-medium hebrew-text" dir="rtl">
            {word.hebrew.text}
          </span>
          <span className="text-muted-foreground">
            {word.translations.en?.text || word.translations.ru?.text}
          </span>
        </div>
        <ChevronRight className="h-4 w-4 text-muted-foreground" />
      </div>
    </Link>
  );
}
